import { createContext } from 'react';
import { User } from 'firebase/auth';
import { SecurityGroup, BusinessGroup, Notification, DocDataWithId, UserSettings, User as SystemUser, NotificationBundleEntry } from '#types/System.types';

/**
 * User profile data from the users collection.
 * Exposes editable fields from the System User type.
 */
export type UserProfileData = Pick<SystemUser, 'name' | 'email' | 'phone'>;

export type NotificationBundlesState = {
  bundlesPath?: string;
  entries: NotificationBundleEntry[];
  unreadNotifications: Notification[];
  readNotifications: Notification[];
  notificationBundleDocIdByEventId: Map<string, string>;
  notificationEntryByEventId: Map<string, NotificationBundleEntry>;
};

/** Represents the target state for a notification */
export enum NotificationReadState {
  READ = 'READ',
  UNREAD = 'UNREAD',
}

export interface HomePageFilters {
  entitySelection: {
    entityId: string | null;
    entityGroupId: string | null;
  } | null;
  dateRange: [string, string];
  selectedFormTypes: string[];
  selectedFormStates: string[];
  filterMode: string;
}

/**
 * Context type providing user authentication and management functionality.
 * Handles user auth state, settings, notifications, and form subscriptions.
 */
export interface UserContextType {
  user: User | null;
  logout: () => Promise<void>;
  isLoggedIn: boolean;
  loading: boolean;
  settings: UserSettings | null;
  updateSettings: (newSettings: UserSettings) => void;
  /** User profile data from the users collection (name, email, phone) */
  userProfile: UserProfileData | null;
  /** Loading state for user profile data */
  userProfileLoading: boolean;
  /** User rights for permissions (formsRead, formsWrite, etc.) */
  userRights: { [rightId: string]: boolean };
  /** Security group IDs the user belongs to (from User.securityGroups) — access-control principals */
  userSecurityGroups: string[];
  /** Business group IDs the user belongs to (from User.businessGroups) — organizational roles, no access */
  userBusinessGroups: string[];
  /** Group metadata from the `securityGroups` collection */
  securityGroups: SecurityGroup[];
  /** Group metadata from the `businessGroups` collection */
  businessGroups: BusinessGroup[];
  hasEntityAccess: boolean;
  notificationBundles?: NotificationBundlesState;
  toggleNotificationRead: (notification: Notification) => Promise<void>;
  deleteNotification: (notification: Notification) => Promise<void>;
  markAllNotificationsRead: () => Promise<void>;
  markAllNotificationsUnread: () => Promise<void>;
  deleteAllNotifications: () => Promise<void>;
  subscribeToForm: (formId: string) => Promise<void>;
  unsubscribeFromForm: (formId: string) => Promise<void>;
  subscribeToEntityRecord: (entityId: string, recordId: string) => Promise<void>;
  unsubscribeFromEntityRecord: (entityId: string, recordId: string) => Promise<void>;
  users: DocDataWithId[];
  assignFormToUser: (formId: string, userId?: string | null) => Promise<void>;
  updateFormSubscribers: (formId: string, userIds: string[]) => Promise<void>;
}

const defaultUserContextValue: UserContextType = {
  user: null,
  logout: async () => {},
  isLoggedIn: false,
  loading: true,
  settings: {
    preferDarkMode: 'system',
    fontSize: 'medium',
  } as UserSettings,
  updateSettings: () => {},
  userProfile: null,
  userProfileLoading: true,
  userRights: {},
  userSecurityGroups: [],
  userBusinessGroups: [],
  securityGroups: [],
  businessGroups: [],
  hasEntityAccess: false,
  notificationBundles: undefined,
  toggleNotificationRead: async () => {},
  deleteNotification: async () => {},
  markAllNotificationsRead: async () => {},
  markAllNotificationsUnread: async () => {},
  deleteAllNotifications: async () => {},
  subscribeToForm: async () => {},
  unsubscribeFromForm: async () => {},
  subscribeToEntityRecord: async () => {},
  unsubscribeFromEntityRecord: async () => {},
  users: [],
  assignFormToUser: async () => {},
  updateFormSubscribers: async () => {},
};

export const UserContext = createContext<UserContextType>(defaultUserContextValue);


